/**
 * Browser-side MP4 -> fragmented MP4 converter (no ffmpeg, no re-encoding).
 *
 * Produces the same structure as:
 *   ffmpeg -i in.mp4 -c copy -movflags frag_keyframe+empty_moov+default_base_moof out.mp4
 *
 *   ftyp + moov (empty sample tables, mvex/trex) + [moof + mdat] per video GOP
 *
 * - A file that is already fragmented is returned untouched.
 * - Fragments start at video keyframes (audio-only files: about every 2 s).
 * - Like ffmpeg's default stream selection, the first video and the first
 *   audio track are kept; other tracks (timecode, subtitles, data) are dropped.
 * - Sample bytes are NOT copied: the result is a Blob that references slices of
 *   the original file, so memory use stays tiny regardless of file size.
 */

const FTYP_BRANDS = ["isom", "iso5", "iso6", "mp41"];
const AUDIO_ONLY_FRAGMENT_S = 2;
const MAX_FRAGMENT_BYTES = 0x7fffffff - (1 << 20);
const FLAGS_SYNC = 0x02000000;
const FLAGS_NON_SYNC = 0x01010000;

/* ----------------------------------------------------------- byte helpers */

const ascii = (s) => Uint8Array.from(s, (c) => c.charCodeAt(0));
const fcc = (dv, o) =>
  String.fromCharCode(dv.getUint8(o), dv.getUint8(o + 1), dv.getUint8(o + 2), dv.getUint8(o + 3));

function concat(list) {
  let total = 0;
  for (const a of list) total += a.length;
  const out = new Uint8Array(total);
  let o = 0;
  for (const a of list) {
    out.set(a, o);
    o += a.length;
  }
  return out;
}

function box(type, ...payload) {
  const body = concat(payload);
  const out = new Uint8Array(8 + body.length);
  new DataView(out.buffer).setUint32(0, out.length);
  out.set(ascii(type), 4);
  out.set(body, 8);
  return out;
}

function u32s(...vals) {
  const out = new Uint8Array(vals.length * 4);
  const dv = new DataView(out.buffer);
  vals.forEach((v, i) => dv.setUint32(i * 4, v >>> 0));
  return out;
}

/* ------------------------------------------------------------ box parsing */

function readBoxes(dv, start, end) {
  const boxes = [];
  let o = start;
  while (o + 8 <= end) {
    let size = dv.getUint32(o);
    const type = fcc(dv, o + 4);
    let header = 8;
    if (size === 1) {
      if (o + 16 > end) break;
      size = Number(dv.getBigUint64(o + 8));
      header = 16;
    } else if (size === 0) {
      size = end - o;
    }
    if (size < header || o + size > end) break;
    boxes.push({ type, start: o, end: o + size, body: o + header });
    o += size;
  }
  return boxes;
}
const kids = (dv, b) => readBoxes(dv, b.body, b.end);
const kid = (dv, b, type) => kids(dv, b).find((k) => k.type === type);

async function scanTopLevel(blob) {
  const boxes = [];
  const total = blob.size;
  let o = 0;
  while (o + 8 <= total) {
    const head = new DataView(await blob.slice(o, Math.min(o + 16, total)).arrayBuffer());
    let size = head.getUint32(0);
    const type = fcc(head, 4);
    if (size === 1) {
      if (head.byteLength < 16) break;
      size = Number(head.getBigUint64(8));
    } else if (size === 0) {
      size = total - o;
    }
    if (size < 8 || o + size > total) {
      if (boxes.some((b) => b.type === "moov")) break; // trailing junk after a valid moov
      throw new Error(`The MP4 looks corrupted: box "${type}" at byte ${o} runs past the end of the file.`);
    }
    boxes.push({ type, start: o, end: o + size });
    o += size;
  }
  return boxes;
}

/* ---------------------------------------------------- sample table reading */

function readSamples(dv, stbl) {
  const stts = kid(dv, stbl, "stts");
  const stsc = kid(dv, stbl, "stsc");
  const stsz = kid(dv, stbl, "stsz");
  const stz2 = kid(dv, stbl, "stz2");
  const stco = kid(dv, stbl, "stco");
  const co64 = kid(dv, stbl, "co64");
  const ctts = kid(dv, stbl, "ctts");
  const stss = kid(dv, stbl, "stss");
  if (!stts || !stsc || !(stsz || stz2) || !(stco || co64)) {
    throw new Error("Unsupported MP4: a track is missing its sample tables.");
  }

  // sizes
  let count;
  let sizes;
  if (stsz) {
    const fixed = dv.getUint32(stsz.body + 4);
    count = dv.getUint32(stsz.body + 8);
    sizes = new Uint32Array(count);
    if (fixed) sizes.fill(fixed);
    else for (let i = 0; i < count; i++) sizes[i] = dv.getUint32(stsz.body + 12 + i * 4);
  } else {
    const field = dv.getUint8(stz2.body + 7);
    count = dv.getUint32(stz2.body + 8);
    sizes = new Uint32Array(count);
    const p = stz2.body + 12;
    for (let i = 0; i < count; i++) {
      if (field === 16) sizes[i] = dv.getUint16(p + i * 2);
      else if (field === 8) sizes[i] = dv.getUint8(p + i);
      else {
        const byte = dv.getUint8(p + (i >> 1));
        sizes[i] = i % 2 === 0 ? byte >> 4 : byte & 15;
      }
    }
  }

  // decode times and durations
  const dts = new Float64Array(count);
  const dur = new Uint32Array(count);
  {
    const entries = dv.getUint32(stts.body + 4);
    let s = 0;
    let t = 0;
    for (let e = 0; e < entries; e++) {
      const c = dv.getUint32(stts.body + 8 + e * 8);
      const d = dv.getUint32(stts.body + 12 + e * 8);
      for (let k = 0; k < c && s < count; k++) {
        dts[s] = t;
        dur[s] = d;
        t += d;
        s++;
      }
    }
    if (s < count) throw new Error("The MP4 looks corrupted: time-to-sample table is shorter than the sample list.");
  }

  // composition offsets (signed, so v0 files that hold negatives also work)
  const cts = new Int32Array(count);
  if (ctts) {
    const entries = dv.getUint32(ctts.body + 4);
    let s = 0;
    for (let e = 0; e < entries; e++) {
      const c = dv.getUint32(ctts.body + 8 + e * 8);
      const off = dv.getInt32(ctts.body + 12 + e * 8);
      for (let k = 0; k < c && s < count; k++) cts[s++] = off;
    }
  }

  // sync samples
  const sync = new Uint8Array(count);
  if (stss) {
    const entries = dv.getUint32(stss.body + 4);
    for (let e = 0; e < entries; e++) {
      const n = dv.getUint32(stss.body + 8 + e * 4);
      if (n >= 1 && n <= count) sync[n - 1] = 1;
    }
  } else {
    sync.fill(1);
  }

  // file offsets (chunk table + samples-per-chunk runs)
  const runCount = dv.getUint32(stsc.body + 4);
  const runFirst = new Uint32Array(runCount);
  const runPer = new Uint32Array(runCount);
  for (let r = 0; r < runCount; r++) {
    runFirst[r] = dv.getUint32(stsc.body + 8 + r * 12);
    runPer[r] = dv.getUint32(stsc.body + 12 + r * 12);
    if (dv.getUint32(stsc.body + 16 + r * 12) > 1) {
      throw new Error("Unsupported MP4: a track uses more than one sample description.");
    }
  }
  const chunkTable = co64 || stco;
  const chunkCount = dv.getUint32(chunkTable.body + 4);
  const offs = new Float64Array(count);
  let s = 0;
  let r = 0;
  for (let c = 0; c < chunkCount && s < count; c++) {
    while (r + 1 < runCount && runFirst[r + 1] <= c + 1) r++;
    let o = co64
      ? Number(dv.getBigUint64(chunkTable.body + 8 + c * 8))
      : dv.getUint32(chunkTable.body + 8 + c * 4);
    for (let k = 0; k < runPer[r] && s < count; k++) {
      offs[s] = o;
      o += sizes[s];
      s++;
    }
  }
  if (s < count) throw new Error("The MP4 looks corrupted: chunk table is shorter than the sample list.");

  return { count, sizes, dts, dur, cts, sync, offs };
}

function parseTrack(dv, bytes, trak) {
  const tkhd = kid(dv, trak, "tkhd");
  const mdia = kid(dv, trak, "mdia");
  if (!tkhd || !mdia) return null;
  const mdhd = kid(dv, mdia, "mdhd");
  const hdlr = kid(dv, mdia, "hdlr");
  const minf = kid(dv, mdia, "minf");
  const stbl = minf && kid(dv, minf, "stbl");
  const stsd = stbl && kid(dv, stbl, "stsd");
  if (!mdhd || !hdlr || !stsd) return null;

  const handler = fcc(dv, hdlr.body + 8);
  if (handler !== "vide" && handler !== "soun") return null;

  const raw = (b) => bytes.subarray(b.start, b.end);
  const tv = dv.getUint8(tkhd.body);
  const mv = dv.getUint8(mdhd.body);
  const edts = kid(dv, trak, "edts");

  const samples = readSamples(dv, stbl);
  if (!samples.count) return null;

  return {
    id: dv.getUint32(tkhd.body + (tv === 1 ? 20 : 12)),
    handler,
    timescale: dv.getUint32(mdhd.body + (mv === 1 ? 20 : 12)),
    tkhd: raw(tkhd),
    edts: edts ? raw(edts) : null,
    mdhd: raw(mdhd),
    hdlr: raw(hdlr),
    minfOthers: kids(dv, minf).filter((b) => b.type !== "stbl").map(raw),
    stsd: raw(stsd),
    ...samples,
  };
}

/* --------------------------------------------------------- box generation */

function buildInitSegment(mvhd, tracks) {
  const ftyp = box("ftyp", ascii("isom"), u32s(512), ...FTYP_BRANDS.map(ascii));

  const traks = tracks.map((t) => {
    const stbl = box(
      "stbl",
      t.stsd,
      box("stts", u32s(0, 0)),
      box("stsc", u32s(0, 0)),
      box("stsz", u32s(0, 0, 0)),
      box("stco", u32s(0, 0))
    );
    const minf = box("minf", ...t.minfOthers, stbl);
    const mdia = box("mdia", t.mdhd, t.hdlr, minf);
    return box("trak", t.tkhd, ...(t.edts ? [t.edts] : []), mdia);
  });

  const mvex = box("mvex", ...tracks.map((t) => box("trex", u32s(0, t.id, 1, 0, 0, 0))));
  return [ftyp, box("moov", mvhd, ...traks, mvex)];
}

function buildTrun(run, dataOffset) {
  const { track, from, to } = run;
  const count = to - from;
  let negative = false;
  for (let i = from; i < to; i++) {
    if (track.cts[i] < 0) {
      negative = true;
      break;
    }
  }
  const out = new Uint8Array(12 + count * 16);
  const dv = new DataView(out.buffer);
  // flags: data-offset | duration | size | sample-flags | composition-offset
  dv.setUint32(0, ((negative ? 1 : 0) << 24) | 0x000f01);
  dv.setUint32(4, count);
  dv.setInt32(8, dataOffset);
  const isVideo = track.handler === "vide";
  let p = 12;
  for (let i = from; i < to; i++) {
    dv.setUint32(p, track.dur[i]);
    dv.setUint32(p + 4, track.sizes[i]);
    dv.setUint32(p + 8, !isVideo || track.sync[i] ? FLAGS_SYNC : FLAGS_NON_SYNC);
    dv.setInt32(p + 12, track.cts[i]);
    p += 16;
  }
  return box("trun", out);
}

function buildMoof(sequence, runs, dataOffsets) {
  const trafs = runs.map((run, j) => {
    const tfhd = box("tfhd", u32s(0x020000, run.track.id)); // default-base-is-moof
    const tfdtBody = new Uint8Array(12);
    const dv = new DataView(tfdtBody.buffer);
    dv.setUint32(0, 0x01000000);
    dv.setBigUint64(4, BigInt(run.track.dts[run.from]));
    return box("traf", tfhd, box("tfdt", tfdtBody), buildTrun(run, dataOffsets[j]));
  });
  return box("moof", box("mfhd", u32s(0, sequence)), ...trafs);
}

/* ---------------------------------------------------------------- planning */

function planFragmentRuns(tracks) {
  const primary = tracks.find((t) => t.handler === "vide") || tracks[0];
  const others = tracks.filter((t) => t !== primary);

  const starts = [0];
  if (primary.handler === "vide") {
    for (let i = 1; i < primary.count; i++) if (primary.sync[i]) starts.push(i);
  } else {
    let last = primary.dts[0] / primary.timescale;
    for (let i = 1; i < primary.count; i++) {
      const t = primary.dts[i] / primary.timescale;
      if (t - last >= AUDIO_ONLY_FRAGMENT_S) {
        starts.push(i);
        last = t;
      }
    }
  }

  const ptr = others.map(() => 0);
  const fragments = [];
  for (let k = 0; k < starts.length; k++) {
    const last = k + 1 >= starts.length;
    const endIdx = last ? primary.count : starts[k + 1];
    const tEnd = last ? Infinity : primary.dts[endIdx] / primary.timescale;
    const runs = [{ track: primary, from: starts[k], to: endIdx }];
    others.forEach((t, j) => {
      const from = ptr[j];
      let p = from;
      while (p < t.count && t.dts[p] / t.timescale < tEnd) p++;
      ptr[j] = p;
      if (p > from) runs.push({ track: t, from, to: p });
    });
    fragments.push(runs);
  }
  return fragments;
}

/** Blob slices for one run of samples; neighbouring samples are merged into one slice. */
function sliceRun(file, run) {
  const { track, from, to } = run;
  const slices = [];
  let start = track.offs[from];
  let end = start + track.sizes[from];
  for (let i = from + 1; i < to; i++) {
    const o = track.offs[i];
    if (o === end) {
      end += track.sizes[i];
    } else {
      slices.push([start, end]);
      start = o;
      end = o + track.sizes[i];
    }
  }
  slices.push([start, end]);
  return slices.map(([a, b]) => {
    if (a < 0 || b > file.size) {
      throw new Error("The MP4 looks corrupted: a sample points past the end of the file.");
    }
    return file.slice(a, b);
  });
}

/* --------------------------------------------------------------- public API */

/**
 * Returns a fragmented MP4 Blob for any MP4 input. Already-fragmented files are
 * returned as-is.
 * @param {File|Blob} file
 * @returns {Promise<File|Blob>}
 */
export async function ensureFragmented(file) {
  if (!file || typeof file.size !== "number" || typeof file.slice !== "function") {
    throw new Error("A file is required.");
  }

  const top = await scanTopLevel(file);
  if (top.some((b) => b.type === "moof")) return file;

  const moovBox = top.find((b) => b.type === "moov");
  if (!moovBox) throw new Error("This is not a valid MP4 (no moov box).");

  const bytes = new Uint8Array(await file.slice(moovBox.start, moovBox.end).arrayBuffer());
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const moov = readBoxes(dv, 0, bytes.length).find((b) => b.type === "moov");
  if (!moov) throw new Error("moov box could not be parsed.");
  if (kid(dv, moov, "cmov")) throw new Error("Unsupported MP4: compressed movie header.");

  const mvhd = kid(dv, moov, "mvhd");
  if (!mvhd) throw new Error("This is not a valid MP4 (no mvhd box).");

  const parsed = kids(dv, moov)
    .filter((b) => b.type === "trak")
    .map((trak) => parseTrack(dv, bytes, trak))
    .filter(Boolean);
  const video = parsed.find((t) => t.handler === "vide");
  const audio = parsed.find((t) => t.handler === "soun");
  const tracks = [video, audio].filter(Boolean);
  if (!tracks.length) throw new Error("This MP4 has no video or audio track that can be played.");

  const parts = buildInitSegment(bytes.subarray(mvhd.start, mvhd.end), tracks);

  let sequence = 1;
  for (const runs of planFragmentRuns(tracks)) {
    const sizes = runs.map((r) => {
      let n = 0;
      for (let i = r.from; i < r.to; i++) n += r.track.sizes[i];
      return n;
    });
    const total = sizes.reduce((a, b) => a + b, 0);
    if (total > MAX_FRAGMENT_BYTES) {
      throw new Error(
        "This video has a section longer than 2 GB without a keyframe, so it cannot be protected. Re-export it with regular keyframes."
      );
    }

    // moof size does not depend on the offset values, so build once to measure.
    const moofSize = buildMoof(sequence, runs, runs.map(() => 0)).length;
    let cursor = moofSize + 8;
    const dataOffsets = sizes.map((n) => {
      const at = cursor;
      cursor += n;
      return at;
    });

    const mdatHeader = new Uint8Array(8);
    new DataView(mdatHeader.buffer).setUint32(0, 8 + total);
    mdatHeader.set(ascii("mdat"), 4);

    parts.push(buildMoof(sequence, runs, dataOffsets), mdatHeader);
    for (const run of runs) for (const s of sliceRun(file, run)) parts.push(s);
    sequence++;
  }

  return new Blob(parts, { type: "video/mp4" });
}